import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import dayjs from 'dayjs';
import { StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { VoucherRegistryModel } from 'entities/voucher/model';
import { Voucher } from 'shared/api';
import { translated } from 'shared/lib';
import { voucherRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import cls from './voucher-registry.module.scss';

export type VoucherRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	tableTdActionSlot?: (voucher: Voucher) => ReactNode;
	topBarActionSlot?: ReactNode;
	emptyActionSlot?: ReactNode;
	filterSlot?: ReactNode;
};

export const createVoucherRegistry = ({ model, $currencies }: VoucherRegistryModel) => {
	const Registry = createRegistry(model);

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'voucher-registry-currencies-filter',
			className: cls.voucherRegistryCurrencyFilter,
			data: $currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			label: translated('voucher-registry.filters.currencies.label'),
			placeholder: translated('voucher-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('voucher-registry.filters.currencies.empty'),
			searchable: true,
			clearable: true,
		},
	});

	const VoucherRegistry = forwardRef<HTMLDivElement, VoucherRegistryProps>(
		(
			{
				withTitle = true,
				emptyActionSlot,
				topBarActionSlot,
				filterSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('voucher-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.voucherRegistryTopBar}>
						<Registry.Filters className={cls.voucherRegistryFilters}>
							<CurrenciesFilter />
						</Registry.Filters>
						<div className={cls.voucherRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col className={cls.voucherRegistryColActions} />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='voucher'>
									{t('voucher-registry.table-columns.voucher')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='currency'>
									{t('voucher-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='deposit'>
									{t('voucher-registry.table-columns.deposit')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='wagering'>
									{t('voucher-registry.table-columns.wagering')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='count'>
									{t('voucher-registry.table-columns.count')}
								</Registry.Table.ThSorter>
								<Registry.Table.Th className={cls.voucherRegistryActions}>
									{t('voucher-registry.table-columns.actions')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({
									variant: row.isLocked ? 'locked' : 'default',
								})}
							>
								{(row) => (
									<>
										<Registry.Table.Td>
											<Anchor
												data-test-id={`voucher-registry-voucher-link-${row.id}`}
												component={Link<StoreValue<typeof voucherRoute.$params>>}
												to={voucherRoute}
												params={{ id: row.id }}
											>
												{dayjs(row.createdAt).format('YYYY-MM-DD HH:mm:ss')}
											</Anchor>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.deposit} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.wagering} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.currentCount} / {row.totalCount}
										</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('voucher-registry.empty.title')}
						message={t('voucher-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('voucher-registry.table-empty.title')}
						message={t('voucher-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return VoucherRegistry;
};
